import React, { useState, useEffect } from 'react';
import type { EventCustomField, FieldType } from '../../types/database';
import {
  getEventCustomFields,
  createEventCustomField,
  updateEventCustomField,
  deleteEventCustomField,
} from '../../lib/api/events';
import { useToast } from '../../contexts/ToastContext';
import { captureError } from '../../lib/capture-error';

interface CustomFieldEditorProps {
  eventId: string;
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
];

const CustomFieldEditor: React.FC<CustomFieldEditorProps> = ({ eventId }) => {
  const { showToast } = useToast();
  const [fields, setFields] = useState<EventCustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<Partial<EventCustomField> | null>(null);
  const [saving, setSaving] = useState(false);

  // Load custom fields
  useEffect(() => {
    const loadFields = async () => {
      try {
        const data = await getEventCustomFields(eventId);
        setFields(data);
      } catch (err) {
          captureError(err, 'Error loading custom fields');
          setError('Failed to load custom fields');
        } finally {
        setLoading(false);
      }
    };

    loadFields();
  }, [eventId]);

  const handleAddField = () => {
    setEditingField({
      field_key: '',
      field_label: '',
      field_type: 'text',
      field_options: null,
      is_required: false,
      placeholder_text: '',
      help_text: '',
      sort_order: fields.length,
      save_to_profile: false,
    });
  };

  const handleEditField = (field: EventCustomField) => {
    setEditingField({ ...field });
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!window.confirm('Are you sure you want to delete this field?')) return;

    try {
      await deleteEventCustomField(fieldId);
      setFields(fields.filter((f) => f.id !== fieldId));
    } catch (err) {
      captureError(err, 'Error deleting field');
      showToast('Failed to delete field', 'error');
    }
  };

  const handleSaveField = async () => {
    if (!editingField) return;

    // Validate
    if (!editingField.field_key?.trim()) {
      showToast('Field key is required', 'error');
      return;
    }
    if (!editingField.field_label?.trim()) {
      showToast('Field label is required', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingField.id) {
        // Update existing field
        const updated = await updateEventCustomField(editingField.id, editingField);
        setFields(fields.map((f) => (f.id === updated.id ? updated : f)));
      } else {
        // Create new field
        const created = await createEventCustomField(eventId, {
          field_key: editingField.field_key!,
          field_label: editingField.field_label!,
          field_type: editingField.field_type || 'text',
          field_options: editingField.field_options || null,
          is_required: editingField.is_required || false,
          placeholder_text: editingField.placeholder_text || null,
          help_text: editingField.help_text || null,
          sort_order: editingField.sort_order || fields.length,
          save_to_profile: editingField.save_to_profile || false,
        });
        setFields([...fields, created]);
      }
      setEditingField(null);
    } catch (err) {
      captureError(err, 'Error saving field');
      showToast('Failed to save field', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOptionsChange = (optionsText: string) => {
    const options = optionsText
      .split('\n')
      .map((o) => o.trim())
      .filter((o) => o);
    setEditingField({
      ...editingField,
      field_options: options.length > 0 ? options : null,
    });
  };

  if (loading) {
    return <div className="text-center py-3">Loading custom fields...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div className="custom-field-editor">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Custom Application Fields</h5>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={handleAddField}
        >
          <i className="fas fa-plus me-1" aria-hidden></i>
          Add Field
        </button>
      </div>

      {fields.length === 0 && !editingField && (
        <p className="text-muted">
          No custom fields yet. Add fields to collect additional information from applicants.
        </p>
      )}

      {/* List of existing fields */}
      {fields.length > 0 && (
        <div className="list-group mb-3">
          {fields.map((field) => (
            <div
              key={field.id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{field.field_label}</strong>
                <span className="text-muted ms-2">({field.field_type})</span>
                {field.is_required && (
                  <span className="badge bg-danger ms-2">Required</span>
                )}
                {field.save_to_profile && (
                  <span className="badge bg-success ms-2">Saves to profile</span>
                )}
                <div className="small text-muted">Key: {field.field_key}</div>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => handleEditField(field)}
                >
                  <i className="fas fa-edit me-1" aria-hidden></i>
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeleteField(field.id)}
                >
                  <i className="fas fa-trash me-1" aria-hidden></i>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create form */}
      {editingField && (
        <div className="card">
          <div className="card-body">
            <h6 className="card-title">
              {editingField.id ? 'Edit Field' : 'New Field'}
            </h6>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Field Key *</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingField.field_key || ''}
                  onChange={(e) =>
                    setEditingField({
                      ...editingField,
                      field_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                    })
                  }
                  placeholder="e.g., weeks_attending"
                />
                <small className="text-muted">
                  Unique identifier (lowercase, underscores only)
                </small>
              </div>
              <div className="col-md-6">
                <label className="form-label">Field Label *</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingField.field_label || ''}
                  onChange={(e) =>
                    setEditingField({ ...editingField, field_label: e.target.value })
                  }
                  placeholder="e.g., Which weeks are you attending?"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Field Type</label>
                <select
                  className="form-select"
                  value={editingField.field_type || 'text'}
                  onChange={(e) =>
                    setEditingField({
                      ...editingField,
                      field_type: e.target.value as FieldType,
                    })
                  }
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Placeholder Text</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingField.placeholder_text || ''}
                  onChange={(e) =>
                    setEditingField({ ...editingField, placeholder_text: e.target.value })
                  }
                  placeholder="e.g., Select an option"
                />
              </div>
            </div>

            {/* Options for select/radio/checkbox */}
            {['select', 'radio', 'checkbox'].includes(editingField.field_type || '') && (
              <div className="mb-3">
                <label className="form-label">Options (one per line)</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={(editingField.field_options || []).join('\n')}
                  onChange={(e) => handleOptionsChange(e.target.value)}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                />
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">Help Text</label>
              <input
                type="text"
                className="form-control"
                value={editingField.help_text || ''}
                onChange={(e) =>
                  setEditingField({ ...editingField, help_text: e.target.value })
                }
                placeholder="Optional guidance shown below the field"
              />
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isRequired"
                  checked={editingField.is_required || false}
                  onChange={(e) =>
                    setEditingField({ ...editingField, is_required: e.target.checked })
                  }
                />
                <label className="form-check-label" htmlFor="isRequired">
                  Required field
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="saveToProfile"
                  checked={editingField.save_to_profile || false}
                  onChange={(e) =>
                    setEditingField({ ...editingField, save_to_profile: e.target.checked })
                  }
                />
                <label className="form-check-label" htmlFor="saveToProfile">
                  Save responses to user profile (for easy apply across events)
                </label>
              </div>
              <small className="text-muted">
                When enabled, user responses will be saved and pre-filled in future applications.
              </small>
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveField}
                disabled={saving}
              >
                {!saving && <i className="fas fa-save me-2" aria-hidden></i>}
                {saving ? 'Saving...' : 'Save Field'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setEditingField(null)}
                disabled={saving}
              >
                <i className="fas fa-times me-2" aria-hidden></i>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldEditor;
